import type { FC } from "react";
import { useEffect, useState, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  Card,
  Box,
  Grid,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
} from "@material-ui/core";
import {
  AddUpdatelanguage,
  deleteLanguage,
  getCountryMaster,
  getLanguageMaster,
  getLanguages,
} from "src/redux/account/connectionServices";
import ChevronRightIcon from "../../icons/ChevronRight";
import AlertMessage from "src/components/ui-components/AlertMessage";
import { IColumn } from "src/components/dataTable/tableModel";
import CellEditTable from "src/components/dataTable/CellEditTable";
import { UpdateTranslation, getTranslation } from "./ConnectionService";
import PencilAltIcon from "../../icons/PencilAlt";
import LanguageModal from "./LanguageModal";
import DeleteIcon from "@material-ui/icons/Delete";
import { Helmet } from "react-helmet-async";

const formattedData = (translations, languageData) => {
  return translations.reduce((acc, curr) => {
    const existingRow = acc.find(
      (row) =>
        row.componentName === curr.componentName &&
        row.translationsKey === curr.translationsKey
    );
    if (existingRow) {
      existingRow[curr.languageKey] = curr.translationValue;
    } else {
      let newRow = {
        id: acc.length,
        componentName: curr.componentName,
        translationsKey: curr.translationsKey,
      };
      languageData.forEach((language) => {
        newRow[language.languageKey] = "";
      });
      newRow[curr.languageKey] = curr.translationValue;
      acc.push(newRow);
    }
    return acc;
  }, []);
};

const TranslationList: FC = () => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [languageData, setLanguageData] = useState([]);
  const isMountedRef = useRef(true);
  const [dataList, setDataList] = useState([]);
  const [columnEditing, setColumnEditing] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [showCreateEditModal, setShowCreateEditModal] =
    useState<boolean>(false);
  const [languageMaster, setLanguageMaster] = useState<any[]>([]);
  const [regionMaster, setRegionMaster] = useState<any[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [languageToDelete, setLanguageToDelete] = useState(null);
  const [oldDataList, setOldDataList] = useState([]);

  const getLanguagesData = async () => {
    const languages = await getLanguages();
    setLanguageData(languages);
  };
  useEffect(() => {
    getLanguagesData();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (languageData.length > 0) {
      fetchTranslationList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageData]);

  const fetchTranslationList = async () => {
    setIsLoading(true);
    const translations = await getTranslation();
    setDataList(formattedData(translations, languageData));
    setIsLoading(false);
  };

  const commitData = async (rowKey, column, editedValue) => {
    if (!columnEditing) {
      const list = dataList[rowKey];
      if (list[column.field] !== editedValue) {
        const requestData = [
          {
            componentName: list.componentName,
            translationsKey: list.translationsKey,
            languageKey: column.field,
            translationValue: editedValue,
          },
        ];

        try {
          const response = await UpdateTranslation(requestData);
          if (response.isSuccess) {
            AlertMessage({ type: "success", msg: response.message });
          } else {
            AlertMessage({
              type: "error",
              msg: "An unexpected error occurred.",
            });
          }
        } catch (error: any) {
          if (error && error.errors) {
            Object.entries(error.errors).forEach(
              ([key, errMsg]: [string, any]) => {
                AlertMessage({ type: "error", msg: errMsg.errorMessage });
              }
            );
          } else {
            AlertMessage({ type: "error", msg: error.title });
          }
        }
      }
    }
  };

  const handleColumnSave = async () => {
    if (columnEditing && Object.keys(editedData).length > 0) {
      setIsLoading(true);
      const requestData = Object.keys(editedData).map((rowKey) => ({
        componentName: dataList[rowKey].componentName,
        translationsKey: dataList[rowKey].translationsKey,
        languageKey: columnEditing,
        translationValue: editedData[rowKey],
      }));

      try {
        const response = await UpdateTranslation(requestData);
        if (response.isSuccess) {
          AlertMessage({ type: "success", msg: response.message });
          setColumnEditing(null);
          setEditedData({});
        } else {
          AlertMessage({ type: "error", msg: "An unexpected error occurred." });
        }
      } catch (error: any) {
        if (error && error.errors) {
          Object.entries(error.errors).forEach(
            ([key, errMsg]: [string, any]) => {
              AlertMessage({ type: "error", msg: errMsg.errorMessage });
            }
          );
        } else {
          AlertMessage({ type: "error", msg: error.title });
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleEditColumn = (field) => {
    const deepCopyOfDataList = JSON.parse(JSON.stringify(dataList));
    setOldDataList(deepCopyOfDataList);
    setColumnEditing(field);
  };

  const handleCancel = () => {
    setIsLoading(true);
    setColumnEditing(null);
    setEditedData({});

    new Promise<void>((resolve) => {
      setDataList(oldDataList);
      resolve();
    }).then(() => {
      setIsLoading(false);
    });
  };

  const handleColumnEdit = (rowKey, column, editedValue) => {
    setEditedData((prevState) => ({
      ...prevState,
      [rowKey]: editedValue,
    }));
    setDataList((prevDataList) => {
      const newDataList = [...prevDataList];
      newDataList[rowKey][column.field] = editedValue;
      return newDataList;
    });
  };

  const handleCreateClick = () => {
    setShowCreateEditModal(true);
  };

  const fetchMasters = async () => {
    const [languagesMasterData, regionsMasterData] = await Promise.all([
      getLanguageMaster(),
      getCountryMaster(),
    ]);
    setLanguageMaster(languagesMasterData);
    setRegionMaster(regionsMasterData);
  };

  useEffect(() => {
    fetchMasters();
  }, []);

  const handleLanguageSave = async (values: any) => {
    try {
      setIsLoading(true);
      const selectedLanguage = languageMaster.find(
        (lang) => lang.language === values.language
      );
      if (selectedLanguage) {
        values.languageCode = selectedLanguage.languageCode;
        values.languageCode2 = selectedLanguage.languageCode2;
      }
      await AddUpdatelanguage(values);
      setShowCreateEditModal(false);
      setIsLoading(false);
      AlertMessage({ type: "success", msg: "Language inserted successfully" });
      await getLanguagesData();
      await fetchTranslationList();
    } catch (err) {
      setIsLoading(false);
      setShowCreateEditModal(false);
      Object.entries(err.errors).forEach(([, errMsg]: any) => {
        AlertMessage({ type: "error", msg: errMsg.errorMessage });
      });
    }
  };

  const handleDeleteClick = (languageKey) => {
    setLanguageToDelete(languageKey);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (languageToDelete) {
      try {
        setIsLoading(true);
        await deleteLanguage(languageToDelete);
        AlertMessage({ type: "success", msg: "Language deleted successfully" });
        await getLanguagesData();
        await fetchTranslationList();
      } catch (err) {
        setIsLoading(false);
        Object.entries(err.errors).forEach(([, errMsg]: any) => {
          AlertMessage({ type: "error", msg: errMsg.errorMessage });
        });
      } finally {
        setIsLoading(false);
        setDeleteDialogOpen(false);
        setLanguageToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setLanguageToDelete(null);
  };

  const validateInput = (value) => {
    const regex =
      /^[\p{L}\-"'!?.:(),][\p{L}\s\-"'!?.:(),]*[\p{L}\-"'!?.:(),]$/u;

    if (value === null || value === "") {
      return "";
    }

    if (value.length > 1 && /^\s+$/.test(value)) {
      return "Please enter a valid input!";
    }

    if (value !== value.trim()) {
      return "Please enter a valid input!";
    }

    if (value.length === 1 && !/[\p{L}\s\-"'!?.:(),]/u.test(value)) {
      return "Please enter a valid input!";
    }

    if (value.length === 1 || regex.test(value)) {
      return "";
    } else {
      return "Please enter a valid input!";
    }
  };

  const columns: IColumn[] = [
    {
      field: "componentName",
      headerName: "Component Name",
      editable: false,
      width: 200,
    },
    {
      field: "translationsKey",
      headerName: "Translation Key",
      editable: false,
      width: 300,
    },
    ...languageData.map((language) => {
      return {
        field: language.languageKey,
        headerName: language.language,
        editable: true,
        maxlength: 1000,
        width: 300,
        editIcon: <PencilAltIcon fontSize="small" />,
        deleteIcon:
          languageData.length > 1 ? <DeleteIcon fontSize="small" /> : null,
      };
    }),
  ];

  return (
    <>
      <Helmet>
        <title>Translations</title>
      </Helmet>
      <Box>
        <Container maxWidth="lg">
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1100,
              opacity: 1,
              width: "100%",
              py: 1,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Grid
              alignItems="center"
              container
              justifyContent="space-between"
              sx={{ my: 2 }}
            >
              <Grid item>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                >
                  <Link
                    color="textPrimary"
                    component={RouterLink}
                    to=""
                    variant="h5"
                  >
                    Configurations
                  </Link>
                  <Typography color="textPrimary" variant="h5">
                    Translations
                  </Typography>
                </Breadcrumbs>
              </Grid>
              <Grid item>
                <Grid alignItems="center" container spacing={2}>
                  <Grid item>
                    {columnEditing === null && (
                      <Button
                        sx={{
                          background:
                            "linear-gradient(to bottom, #072f40, #072f40)",
                          minWidth: 100,
                        }}
                        variant="contained"
                        onClick={handleCreateClick}
                      >
                        Add New Language
                      </Button>
                    )}
                  </Grid>
                  <Grid item>
                    {columnEditing !== null && (
                      <Button
                        sx={{
                          background:
                            "linear-gradient(to bottom, #939ca3, #939ca3)",
                          minWidth: 100,
                        }}
                        variant="contained"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    )}
                  </Grid>
                  <Grid item>
                    {columnEditing !== null &&
                      Object.keys(editedData).length > 0 && (
                        <Button
                          sx={{
                            background:
                              "linear-gradient(to bottom, #072f40, #072f40)",
                            minWidth: 100,
                          }}
                          variant="contained"
                          onClick={handleColumnSave}
                        >
                          Save
                          {isLoading && (
                            <CircularProgress
                              color="inherit"
                              thickness={5}
                              size={20}
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Button>
                      )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Card>
            <CellEditTable
              columns={columns}
              dataList={dataList}
              isLoading={isLoading}
              commitData={commitData}
              setDataList={setDataList}
              handleEditColumn={handleEditColumn}
              columnEditing={columnEditing}
              handleColumnEdit={handleColumnEdit}
              onColumnDelete={handleDeleteClick}
              validateInput={validateInput}
            />
          </Card>
        </Container>
      </Box>
      <LanguageModal
        open={showCreateEditModal}
        handleClose={() => setShowCreateEditModal(false)}
        handleLanguageSave={handleLanguageSave}
        languageMaster={languageMaster.filter(
          (lang) =>
            !languageData.some(
              (langData) => langData.language === lang.language
            )
        )}
        regionMaster={regionMaster}
      />
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this Language? All translations of
            this language will also be deleted. This action will permanently
            delete data. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            No
          </Button>
          <Button onClick={handleDeleteConfirm} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TranslationList;
